<template>
  <w-sliding-popup ref="slidingPopup" :title="title" width="1250px" @close="onClose">
    <div slot="content">
      <el-row type="flex" :gutter="15" justify="space-between">
        <!-- 전체 노드 목록 -->
        <el-col :span="12">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box title-h3">전체 노드 목록</div>
            <div class="vertical-center-box" align="right">
              <w-header-search-button @click="showNodeAllSearchTool = !showNodeAllSearchTool"></w-header-search-button>
            </div>
          </el-row>
          <el-table-wrapper ref="nodeTable" :column-default="columnDefault" :data="nodeList" :columns="nodeTableColumns" :show-search-tool="showNodeAllSearchTool" :show-custom-header="true"
          row-class-name="c-dblclick" remote pagination :total="total" @condition-change="loadData('node')"
          @select="onSelect" @select-all="onSelectAll" @pagination-change="handlerPaginationChange" @total-change="handlerPaginationChange">
          </el-table-wrapper>
        </el-col>
        <!-- 선택한 노드 목록 -->
        <el-col :span="12">
          <el-row class="title-bar" type="flex" justify="space-between">
            <div class="vertical-center-box title-h3">선택한 노드 목록</div>
            <div class="vertical-center-box" align="right">
              <el-button class="mr-r4" :disabled="checkedRows.length === 0" @click="onReleaseButtonClick">해제</el-button>
              <w-header-search-button size="mini" @click="showNodeSearchTool = !showNodeSearchTool"></w-header-search-button>
            </div>
          </el-row>
          <el-table-wrapper ref="trgNodeTable" :column-default="columnDefault" :data="trgNodeList" :columns="trgNodeTableColumns"
          :show-search-tool="showNodeSearchTool" :show-custom-header="true" pagination
          @selection-change="changeCheckedRow" @pagination-change="handlerPaginationChange" @total-change="handlerPaginationChange">
            <template slot-scope="scope" slot="detail-slot">
              <span v-show="saveButtonDisabled"><i class="far fa-search-plus c-pointer" @click.stop="onClickDetailButton(scope.row)"></i></span>
            </template>
          </el-table-wrapper>
        </el-col>
      </el-row>

      <w-trigger-node-var-setting-popup :selected-trg-job="selectedTrgJob" :selected-trg-node="selectedTrgNode" ref="varSettingPopup"></w-trigger-node-var-setting-popup>
    </div>
    <div slot="footer">
      <el-button type="solid" size="large" @click="onSaveButtonClick">저장</el-button>
      <el-button size="large" @click="onCancelButtonClick">취소</el-button>
    </div>
  </w-sliding-popup>
</template>

<script>
import { cloneDeep, map, difference, isNil } from 'lodash';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import msgUtil from '@/utils/message';
import { columnDefault, defaultPagination, defaultDetailHeaderColumn } from '@/constants/table';
import AmsExecutionAPI from '@/api/ams/execution';
import WTriggerNodeVarSettingPopup from './TriggerNodeVarSettingPopup';

const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);
const pagination = cloneDeep(defaultPagination);

export default {
  name: 'WTriggerNodeSettingPopup',
  components: { WTriggerNodeVarSettingPopup },
  props: {
    selectedTrigger: {
      type: Object,
    },
    selectedTrgJob: {
      type: Object,
      default: () => ({ jobId: EMPTY_TEXT, jobName: EMPTY_TEXT }),
    },
    triggerNodeList: {
      tyupe: Array,
      default: () => [],
    },
  },
  computed: {
    trgNodeIds() {
      return map(this.trgNodeList, (e) => e.nodeId);
    },
    saveButtonDisabled() {
      const equalLength = this.originTrgNodeIds.length === this.trgNodeIds.length;
      return equalLength && difference(this.originTrgNodeIds, this.trgNodeIds).length === 0;
    },
    title() {
      let title = EMPTY_TEXT;
      if (!isNil(this.selectedTrgJob)) {
        title = `[${this.selectedTrgJob.jobName}] 장비 설정`;
      }
      return title;
    },
  },
  data() {
    return {
      pagination,
      columnDefault,
      total: 0,
      showNodeAllSearchTool: false,
      showNodeSearchTool: false,
      nodeTableColumns: [
        { type: 'selection' },
        { prop: 'nodeName', label: '노드 명', remoteProp: 'node_name', searchable: true, sortable: true },
        { prop: 'nodeIp', label: '노드 IP 주소', remoteProp: 'node_ip', searchable: true, sortable: true, formatter: defaultFormatter },
      ],
      trgNodeTableColumns: [
        { type: 'selection' },
        { prop: 'nodeName', label: '노드 명', searchable: true, sortable: true },
        { prop: 'nodeIp', label: '노드 IP 주소', searchable: true, sortable: true, formatter: defaultFormatter },
        { ...defaultDetailHeaderColumn, scopedSlot: 'detail-slot' },
      ],
      nodeList: [],
      trgNodeList: [],
      originTrgNodeIds: [],
      checkedRows: [],
      selectedTrgNode: null,
    };
  },
  watch: {
    triggerNodeList(arr) {
      this.trgNodeList = cloneDeep(arr);
      this.originTrgNodeIds = map(arr, (e) => e.nodeId);
    },
  },
  methods: {
    open() {
      this.$refs.slidingPopup.visible = true;
      this.loadData();
    },
    close() {
      this.$refs.slidingPopup.visible = false;
    },
    onClose() {
      this.$refs.nodeTable.getTable().clearSelection();
      this.$refs.trgNodeTable.getTable().clearSelection();
    },
    loadData() {
      const me = this;
      me.$nextTick(() => {
        const tableParams = me.$refs.nodeTable.getParameter();
        AmsExecutionAPI.getNodeList(tableParams).then((response) => {
          if (response.data.success) {
            this.nodeList = response.data.data;
          }
        }).finally(() => {
          this.toggleNodeRowSelection();
        });
      });
    },
    onClickDetailButton(row) {
      this.selectedTrgNode = row;
      this.$nextTick(() => {
        this.$refs.varSettingPopup.open();
      });
    },
    onSelect(selection) {
      this.trgNodeList = selection;
    },
    onSelectAll(selection) {
      this.trgNodeList = selection;
    },
    changeCheckedRow(selection) {
      this.checkedRows = selection;
    },
    handlerPaginationChange(_pagination) {
      this.pagination = _pagination;
    },
    onSaveButtonClick() {
      const trgNodeIds = this.trgNodeIds.sort();
      const triggerId = this.selectedTrgJob.triggerId;
      const jobId = this.selectedTrgJob.jobId;
      const insertTrgNodeParams = difference(trgNodeIds, this.originTrgNodeIds).map((nodeId) => ({ type: 'INSERT', triggerId, jobId, nodeId }));
      const deleteTrgNodeParams = difference(this.originTrgNodeIds, trgNodeIds).map((nodeId) => ({ type: 'DELETE', triggerId, jobId, nodeId }));

      const target = '노드';
      const action = msgUtil.action.SAVE;

      if (insertTrgNodeParams.length === 0 && deleteTrgNodeParams.length === 0) {
        this.$message(msgUtil.getResultMsgData(action, target, true));
        this.close();
        return;
      }
      AmsExecutionAPI.updateTriggerNodeList([...insertTrgNodeParams, ...deleteTrgNodeParams]).then((response) => {
        if (response.data.success) {
          this.$message(msgUtil.getResultMsgData(action, target, true));
          this.$emit('re-load');
          this.close();
        } else {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(action, target, false));
        throw new Error(e);
      });
    },
    /**
     * 취소 버튼 click
     * @author jeee12
     */
    onCancelButtonClick() {
      this.close();
    },
    /**
     * 해제 버튼 click
     * @author jeee12
     * @return {[type]} [description]
     */
    onReleaseButtonClick() {
      this.checkedRows.forEach((row) => {
        this.trgNodeList.splice(this.trgNodeList.indexOf(row), 1);
      });
      this.toggleNodeRowSelection();
    },
    toggleNodeRowSelection() {
      this.nodeList.forEach((e) => {
        const isExist = this.trgNodeIds.indexOf(e.nodeId) > -1;
        this.$refs.nodeTable.getTable().toggleRowSelection(e, isExist);
      });
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/work/popup/components/ExecuteTrigger/popup/TriggerNodeSettingPopup.vue